import React, { useState, useEffect, useMemo } from 'react';
import NotificationDialog from './NotificationDialog';
import JobDetailsModal from './JobDetailsModal';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useMultilingualText, COMMON_TEXT_KEYS } from '../hooks/useMultilingualText';
import { getTotalUnreadNotificationCount } from '../services/apiService';
import { getCandidateId } from '../services/authService';

const Header = () => {
    const { isAuthenticated, currentUser, showSignIn } = useAuth();
    
    // Multilingual text support - memoize textKeys to prevent re-renders
    const textKeys = useMemo(() => [
        COMMON_TEXT_KEYS.MY_APPLICATIONS,
        COMMON_TEXT_KEYS.MY_REFERRALS,
        COMMON_TEXT_KEYS.MY_EARNINGS,
        COMMON_TEXT_KEYS.SIGN_IN
    ], []);
    
    const { getText } = useMultilingualText(textKeys);
    const location = useLocation();
    const navigate = useNavigate();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [unreadCount, setUnreadCount] = useState(0);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [showJobDetailsModal, setShowJobDetailsModal] = useState(false);
    const [selectedJobForDetails, setSelectedJobForDetails] = useState(null);
    const [isNotificationFocused, setIsNotificationFocused] = useState(false);
    

    // Fetch unread count and persist/cache
    const refreshUnread = async () => {
        try {
            // Use candidateId from auth context/storage; do not call profile API here
            const cid = (typeof getCandidateId === 'function') ? getCandidateId() : null;
            if (!cid) return;
            const res = await getTotalUnreadNotificationCount(cid);
            if (res?.success) {
                const count = Number(res.data || 0);
                setUnreadCount(count);
                try { localStorage.setItem('unreadCount', String(count)); } catch (_) {}
            }
        } catch (e) {
            // silent
        }
    };

    // Initialize unread count from cache written by Homepage
    useEffect(() => {
        try {
            const cached = localStorage.getItem('unreadCount');
            if (cached !== null) setUnreadCount(Number(cached) || 0);
        } catch (_) { /* ignore */ }
    }, []);

    // Only refresh unread count when notification dialog is open
    useEffect(() => {
        if (!isModalOpen) return;
        // Fetch immediately on open
        refreshUnread();
        // Poll while open
        const interval = setInterval(refreshUnread, 60_000);
        return () => clearInterval(interval);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isModalOpen]);

    // Listen for global unread count updates (e.g., after job apply)
    useEffect(() => {
        const handler = (e) => {
            const next = (e && e.detail && typeof e.detail.count === 'number') ? e.detail.count : null;
            if (next !== null) {
                setUnreadCount(next);
                try { localStorage.setItem('unreadCount', String(next)); } catch (_) {}
            } else {
                // Fallback: read from storage
                try {
                    const cached = localStorage.getItem('unreadCount');
                    if (cached !== null) setUnreadCount(Number(cached) || 0);
                } catch (_) {}
            }
        };
        window.addEventListener('unreadCountUpdated', handler);
        return () => window.removeEventListener('unreadCountUpdated', handler);
    }, []);

    // Listen for showJobDetailsModal custom event from notifications
    useEffect(() => {
        const handleShowJobDetailsModal = (event) => {
            console.log('Header received showJobDetailsModal event:', event.detail);
            const jobData = event.detail?.job;
            if (jobData) {
                setSelectedJobForDetails(jobData);
                setShowJobDetailsModal(true);
                // Close notification dialog if it's open
                setIsModalOpen(false);
            }
        };
        
        window.addEventListener('showJobDetailsModal', handleShowJobDetailsModal);
        return () => window.removeEventListener('showJobDetailsModal', handleShowJobDetailsModal);
    }, []);

    const toggleNotifications = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        console.log('Notification clicked, current state:', isModalOpen);
        
        // If notification is not focused, set it as focused and remove menu focus
        if (!isNotificationFocused) {
            setIsNotificationFocused(true);
            setIsModalOpen(true);
            
            // Remove focus from menu items by navigating to home if on a menu page
            const menuPages = ['/applications', '/referrals', '/earnings', '/profile'];
            if (menuPages.includes(location.pathname)) {
                navigate('/');
            }
            
            // refresh unread count on open
            await refreshUnread();
        } else {
            // If notification is already focused, just toggle the dialog
            setIsModalOpen(!isModalOpen);
        }
    };

    

    // Close dropdowns when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            // Don't close notification dialog when clicking outside
            // Only close when notification icon is clicked again
            
            // Close mobile menu if clicking outside
            if (!event.target.closest('.mobile-menu-toggle') && !event.target.closest('.mobile-menu') && isMobileMenuOpen) {
                setIsMobileMenuOpen(false);
            }
        };

        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, [isModalOpen, isMobileMenuOpen]);

    const handleSignInClick = () => {
        showSignIn();
    };

    const toggleMobileMenu = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsMobileMenuOpen(!isMobileMenuOpen);
    };





    // Derive initials for profile icon
    const profileLetter = useMemo(() => {
        const getInitials = (fullName) => {
            if (!fullName || typeof fullName !== 'string') return '';
            const names = fullName.trim().split(/\s+/).filter(name => name.length > 0);
            
            if (names.length === 0) return '';
            if (names.length === 1) return names[0].charAt(0).toUpperCase();
            if (names.length === 2) return names[0].charAt(0).toUpperCase() + names[1].charAt(0).toUpperCase();
            // For 3 or more names, use first, middle, and last
            return names[0].charAt(0).toUpperCase() + names[1].charAt(0).toUpperCase() + names[names.length - 1].charAt(0).toUpperCase();
        };

        if (!currentUser) return 'U';
        
        // Try common name fields
        const fullName = currentUser.name || currentUser.fullName || currentUser.firstName;
        if (fullName) {
            const initials = getInitials(fullName);
            if (initials) return initials;
        }
        
        // Try email as fallback
        if (currentUser.email && typeof currentUser.email === 'string') {
            const emailInitial = currentUser.email.trim().charAt(0).toUpperCase();
            if (emailInitial) return emailInitial;
        }
        
        // Try mobile as last resort
        if (currentUser.mobileNumber && typeof currentUser.mobileNumber === 'string') {
            const mobileInitial = currentUser.mobileNumber.trim().charAt(0);
            if (mobileInitial) return mobileInitial;
        }
        
        return 'U';
    }, [currentUser]);

    const handleJobDetailsModalClose = () => {
        setShowJobDetailsModal(false);
        setSelectedJobForDetails(null);
    };

    return (
        <>
        <nav className="top-nav">
            <div className="nav-container">
                {/* Mobile hamburger menu button - moved to left */}
                <button className="mobile-menu-toggle" onClick={toggleMobileMenu}>
                    <i className="fas fa-bars"></i>
                </button>
                
                <Link to="/" className="logo">
                    <img
                        src="/images/custom-header.png"
                        alt="Channelplay"
                        style={{ width: 36, height: 36, objectFit: 'contain', display: 'block' }}
                    />
                    <span>Channelplay Jobs</span>
                </Link>
                
                <div className="nav-right-section">
                    <div className="nav-links">
                        {isAuthenticated ? (
                            <>
                                <NavLink to="/applications" onClick={(e) => { refreshUnread(); setIsModalOpen(false); setIsNotificationFocused(false); }} className={({ isActive }) => isActive ? "nav-link active" : "nav-link"}>
                                    <i className="fas fa-file-alt"></i>
                                    {getText(COMMON_TEXT_KEYS.MY_APPLICATIONS, 'My Applications')}
                                </NavLink>
                                <NavLink to="/referrals" onClick={(e) => { refreshUnread(); setIsModalOpen(false); setIsNotificationFocused(false); }} className={({ isActive }) => isActive ? "nav-link active" : "nav-link"}>
                                    <i className="fas fa-user-friends"></i>
                                    {getText(COMMON_TEXT_KEYS.MY_REFERRALS, 'My Referrals')}
                                </NavLink>
                                <NavLink 
                                    to="/earnings" 
                                    onClick={(e) => { 
                                        refreshUnread(); 
                                        setIsModalOpen(false); 
                                        setIsNotificationFocused(false);
                                    }} 
                                    className={({ isActive }) => isActive ? "nav-link active" : "nav-link"}
                                >
                                    <i className="fas fa-wallet"></i>
                                    {getText(COMMON_TEXT_KEYS.MY_EARNINGS, 'My Earnings')}
                                </NavLink>
                            </>
                        ) : (
                            <>
                                <button onClick={handleSignInClick} className="nav-link nav-link-button">
                                    <i className="fas fa-file-alt"></i>
                                    {getText(COMMON_TEXT_KEYS.MY_APPLICATIONS, 'My Applications')}
                                </button>
                                <button onClick={handleSignInClick} className="nav-link nav-link-button">
                                    <i className="fas fa-user-friends"></i>
                                    {getText(COMMON_TEXT_KEYS.MY_REFERRALS, 'My Referrals')}
                                </button>
                                <button onClick={handleSignInClick} className="nav-link nav-link-button">
                                    <i className="fas fa-wallet"></i>
                                    {getText(COMMON_TEXT_KEYS.MY_EARNINGS, 'My Earnings')}
                                </button>
                            </>
                        )}
                    </div>
                    <div className="nav-right" style={{ marginLeft: '-16px' }}>

                        {/* Language selector removed as requested */}

                        {isAuthenticated ? (
                            <>
                                <div className="notification-wrapper">
                                    <button className={`header-notification-icon ${isNotificationFocused ? 'active' : ''}`} onClick={toggleNotifications}>
                                        <i className="fas fa-bell"></i>
                                    </button>
                                    {unreadCount > 0 && (
                                        <span className="badge">{unreadCount}</span>
                                    )}
                                    {isModalOpen && (
                                        <NotificationDialog 
                                            isOpen={isModalOpen} 
                                            onClose={() => setIsModalOpen(false)}
                                            onAnyRead={refreshUnread}
                                        />
                                    )}
                                </div>
                                <Link to="/profile" onClick={(e) => { 
                                    refreshUnread(); 
                                    setIsModalOpen(false); 
                                    setIsNotificationFocused(false);
                                }} className={`profile-icon ${location.pathname === '/profile' ? 'active' : ''}`} style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: '30px',
                                    height: '30px',
                                    background: location.pathname === '/profile' ? '#10b981' : '#2563eb',
                                    color: 'white',
                                    borderRadius: '50%',
                                    textDecoration: 'none',
                                    fontWeight: '600',
                                    fontSize: '0.8rem',
                                    transition: 'all 0.3s ease',
                                    boxShadow: location.pathname === '/profile' ? '0 2px 4px rgba(16, 185, 129, 0.3)' : '0 2px 4px rgba(37, 99, 235, 0.3)',
                                    marginLeft: '8px'
                                }}>
                                    <span>{profileLetter}</span>
                                </Link>
                            </>
                        ) : (
                            <button className="sign-in-btn" onClick={handleSignInClick} style={{ marginLeft: '0' }}>
                                <i className="fas fa-sign-in-alt"></i>
                                {getText(COMMON_TEXT_KEYS.SIGN_IN, 'Sign In')}
                            </button>
                        )}
                    </div>
                </div>
                
                {/* Mobile menu dropdown */}
                <div className={`mobile-menu ${isMobileMenuOpen ? 'open' : ''}`}>
                    {isAuthenticated ? (
                        <>
                            <NavLink to="/applications" onClick={() => { refreshUnread(); setIsMobileMenuOpen(false); setIsModalOpen(false); setIsNotificationFocused(false); }} className={({ isActive }) => isActive ? "nav-link active" : "nav-link"}>
                                <i className="fas fa-file-alt"></i>
                                {getText(COMMON_TEXT_KEYS.MY_APPLICATIONS, 'My Applications')}
                            </NavLink>
                            <NavLink to="/referrals" onClick={() => { refreshUnread(); setIsMobileMenuOpen(false); setIsModalOpen(false); setIsNotificationFocused(false); }} className={({ isActive }) => isActive ? "nav-link active" : "nav-link"}>
                                <i className="fas fa-user-friends"></i>
                                {getText(COMMON_TEXT_KEYS.MY_REFERRALS, 'My Referrals')}
                            </NavLink>
                            <NavLink to="/earnings" onClick={() => { refreshUnread(); setIsMobileMenuOpen(false); setIsModalOpen(false); setIsNotificationFocused(false); }} className={({ isActive }) => isActive ? "nav-link active" : "nav-link"}>
                                <i className="fas fa-wallet"></i>
                                {getText(COMMON_TEXT_KEYS.MY_EARNINGS, 'My Earnings')}
                            </NavLink>
                        </>
                    ) : (
                        <>
                            <button onClick={() => { handleSignInClick(); setIsMobileMenuOpen(false); }} className="nav-link nav-link-button">
                                <i className="fas fa-file-alt"></i>
                                {getText(COMMON_TEXT_KEYS.MY_APPLICATIONS, 'My Applications')}
                            </button>
                            <button onClick={() => { handleSignInClick(); setIsMobileMenuOpen(false); }} className="nav-link nav-link-button">
                                <i className="fas fa-user-friends"></i>
                                {getText(COMMON_TEXT_KEYS.MY_REFERRALS, 'My Referrals')}
                            </button>
                            <button onClick={() => { handleSignInClick(); setIsMobileMenuOpen(false); }} className="nav-link nav-link-button">
                                <i className="fas fa-wallet"></i>
                                {getText(COMMON_TEXT_KEYS.MY_EARNINGS, 'My Earnings')}
                            </button>
                            <button onClick={() => { handleSignInClick(); setIsMobileMenuOpen(false); }} className="nav-link nav-link-button">
                                <i className="fas fa-sign-in-alt"></i>
                                {getText(COMMON_TEXT_KEYS.SIGN_IN, 'Sign In')}
                            </button>
                        </>
                    )}
                </div>
            </div>
        </nav>
        
        {/* Global JobDetailsModal for notifications */}
        {showJobDetailsModal && selectedJobForDetails && (
            <JobDetailsModal
                isOpen={showJobDetailsModal}
                onClose={handleJobDetailsModalClose}
                job={selectedJobForDetails}
            />
        )}
        </>
    );
};

export default Header;
